import { useState } from "react";
import { View } from "react-native";
import { Text } from "react-native-paper";
import { useForm } from "react-hook-form";
import { gql, useMutation } from "@apollo/client";

import Button from "../../components/Button";
import { useErrorScroll } from "../../components/ErrorMessage";
import ReasonDropdown from "../../components/ReasonDropdown";
import useSnackBar from "../../hooks/useSnackBar";
import Modal from "../../components/Modal";
import ActionsContainer from "../../components/ActionsContainer";

type FormData = {
  reason: string;
};

const defaultValues: FormData = {
  reason: "",
};

export const MUTATION = gql`
  mutation cancelDeferredCompSubmission($id: ID!, $reason: String!) {
    cancelDeferredCompSubmission(id: $id, reason: $reason) {
      message
      success
      submission {
        id
        status
      }
    }
  }
`;

type Props = {
  visible: boolean;
  onSuccess(): void;
  onDismissModal(): void;
  id: string;
};

export default function MyActivityCancelRequestModal({ id, visible, onSuccess, onDismissModal }: Props) {
  const [sendMutation, { loading }] = useMutation(MUTATION);

  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting },
  } = useForm<FormData>({ defaultValues, mode: "onChange" });
  const [apiError, setApiError] = useState("");
  const { onSubmitError } = useErrorScroll({ defaultValues });
  const showSnackBar = useSnackBar();

  const onSubmit = async ({ reason }: FormData) => {
    try {
      const {
        data: {
          cancelDeferredCompSubmission: { success, message },
        },
      } = await sendMutation({
        variables: {
          id,
          reason,
        },
      });
      if (success) {
        setTimeout(() => {
          showSnackBar({ message: "Activity Cancelled", duration: 3000 });
        }, 1000);
        onDismissModal();
        onSuccess();
      } else {
        throw new Error(message);
      }
    } catch (e: any) {
      setApiError((e as Error).message || "Something went wrong");
    }
  };

  return (
    <Modal {...{ visible, onDismiss: onDismissModal, title: "Cancel Request" }}>
      <View className="flex-1 bg-white" testID="MyActivityCancelRequestModal">
        <View className="m-6">
          <Text className="text-regular-normal font-400 text-ink-dark mb-6">
            Please select the reason you are canceling this activity review request.
          </Text>
          <ReasonDropdown
            reasons={[
              "Incorrect activity",
              "Incorrect date of the activity",
              "Incorrect description",
              "Incorrect participation level",
              "Other",
            ]}
            control={control}
            name="reason"
            label="Select Reason"
            rules={{ required: "required" }}
          />
        </View>

        <ActionsContainer
          errorAlert
          errorMessage={apiError}
          onCloseErrorMessage={setApiError}
          horizontalStyle="all"
          insideModal
        >
          <Button onPress={onDismissModal}>Go Back</Button>
          <Button
            loading={loading}
            disabled={isSubmitting}
            isValid={isValid}
            onPress={handleSubmit(onSubmit, onSubmitError)}
            mode="contained"
            testID="cancel-request-mutation-button"
          >
            Cancel Request
          </Button>
        </ActionsContainer>
      </View>
    </Modal>
  );
}
